import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import {
  Field,
  Form,
  checkBox,
  constantField,
  numberBox,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import {
  formulaid,
  instrumentid,
  payment_scheme_id,
  payment_scheme_non_periodic_id,
  planningmode,
  questiontype,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { addInfoUnderField } from "../../client/addinfounderfield";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import {
  checkboxesField,
  wrapSelectboxesInPanel,
} from "../../client/checkboxesfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import {
  currencyBox,
  formulaInvoicelinesForm,
} from "../../client/financialfields";
import { radioField } from "../../client/radiofield";
import { renderDeleteButton, renderSaveButton } from "../../client/savebutton";
import { country, formulaFull } from "../../domain/config";
import { calculateAmountWithoutVat } from "../../domain/finance";
import { invoicelineFormula } from "../../domain/finance/types";
import { isNil } from "../../lib/istype";
import { selectBoxNullable } from "../../lib/selectboxnullablefield";
import { mandatoryValidation } from "../../lib/validate";
import { allFormulaColors, formulaType } from "../../parsing_from_ur";
import {
  renderCurrency,
  renderFormulaTypeShort,
  renderPlanningMode,
  showFormulaChargeMode,
} from "../../render";
import * as routes from "../../routes";
import { saveFormulaSpec } from "../../routes";
import { translateFormulaColor } from "../../translations_specific";
import { errorField, lessonDurationOptions } from "../planning/lessongroupform";
import { match } from "ts-pattern";

export type formulaForEditing = Omit<
  formulaFull,
  "id" | "questions" | "payment_schemes"
> & {
  id: formulaid | null;
  questions: {
    label: string;
    questiontype: questiontype;
    mandatory: boolean;
    select_options: string[] | null;
  }[];
  payment_schemes: payment_scheme_id[];
};

export function render(opts: {
  formula: formulaForEditing;
  hasOnlinePayments: boolean;
  periodfeeAmountWithoutDiscounts: currency;
  allInstruments: { id: instrumentid; description: string }[];
  country: country;
  questionsActivated: boolean;
  campsActivated: boolean;
  paymentSchemes: {
    id: payment_scheme_id;
    label: string;
    payment_scheme_non_periodic_id: null | payment_scheme_non_periodic_id;
  }[];
}): HTMLElement | HTMLElement[] {
  const formula = opts.formula;
  const id = formula.id;

  const memoryMaxOneOptionalLine = new Source(formula.max_one_optional_line);

  const form = new Form({})
    .addField("id", [], () => constantField(formula.id))
    .addField("description", [], () =>
      textBox({
        initialVal: formula.description,
        label: "Naam",
        validations: [mandatoryValidation],
      }),
    )
    .addField("color", [], () =>
      selectBox({
        values: allFormulaColors,
        initial: formula.color,
        show: translateFormulaColor,
        label: "Kleur op kalender",
      }),
    )
    .addField("comments", [], async () => {
      const field = await textBox({
        initialVal: formula.comments,
        label: "Extra beschrijving",
      });

      const commentsLabel = "De extra beschrijving is bestemd voor extra informatie die je aan je studenten wilt laten weten over de formule, bijvoorbeeld dat een privéles formule ook enkele groeplessen bevat, of dat in een formule ook een kennismakingsmoment zit. Dit past niet altijd in de naam van de formule, omdat die anders te lang zou worden.";
      return addInfoUnderField(field, commentsLabel);
    })
    .addField("formulatypetag", [], () =>
      selectBox({
        values: [
          "Group" as const,
          "Private" as const,
          ...(opts.campsActivated ? ["Camp" as const] : []),
        ],
        initial: formula.type_.tag,
        show: (g) => renderFormulaTypeShort({ tag: g }),
        label: "Soort les",
      }),
    )
    .addField(
      "maxnumberofstudents",
      ["formulatypetag"],
      async ({ formulatypetag }) => {
        if (formulatypetag === "Group" || formulatypetag === "Camp") {
          const field = await numberBox(
            formula.type_.tag === "Group"
              ? formula.type_.maxnumberofstudents
              : formula.type_.tag === "Camp"
              ? formula.type_.maxnumberofstudentscamp
              : 5,
            { label: "Maximum aantal studenten" },
          );
          return addInfoUnderField(
            field,
            "Dit geldt enkel voor online inschrijvingen. Maak je als admin een inschrijving dan geldt deze beperking niet.",
          );
        } else {
          return constantField(1);
        }
      },
    )
    .addField(
      "type_",
      ["formulatypetag", "maxnumberofstudents"],
      ({
        formulatypetag,
        maxnumberofstudents,
      }): Promise<Field<formulaType>> => {
        if (
          (formulatypetag === "Group" || formulatypetag === "Camp") &&
          maxnumberofstudents < 2
        ) {
          return errorField("Moet 2 of meer zijn");
        }
        return constantField<formulaType>(
          formulatypetag === "Private"
            ? { tag: "Private" }
            : formulatypetag === "Camp"
            ? { tag: "Camp", maxnumberofstudentscamp: maxnumberofstudents }
            : formulatypetag === "Group"
            ? { tag: "Group", maxnumberofstudents }
            : checkAllCasesHandled(formulatypetag),
        );
      },
    )
    .addField("numberoflessons", [], () =>
      numberBox(formula.numberoflessons, { label: "Aantal lessen" }),
    )
    .addField("lessonduration", [], () =>
      selectBox({
        values: lessonDurationOptions,
        initial: formula.lessonduration,
        show: (n) => n.toString() + " " + "minuten",
        label: "Lesduur",
      }),
    )
    .addField("termsurl", [], async () =>
      addInfoUnderField(
        await textBox({
          initialVal: formula.termsurl || "",
          label: "Link naar reglement specifiek voor deze formule",
          validations: [
            (s) =>
              s.trim() === "" ||
              s.toLowerCase().includes("http://") ||
              s.toLowerCase().includes("https://")
                ? {
                    tag: "parsed",
                    parsed: s.trim(), // .toLowerCase() Don't lowercase, some URL's are case sensitive
                  }
                : {
                    tag: "err",
                    label: "De link moet http:// of https:// bevatten",
                  },
          ],
        }),
        "Deze link zal getoond worden tijdens een online inschrijving en heeft voorrang op het algemene schoolreglement. Dit moet steeds met http:// of https:// beginnen.",
      ),
    )
    .addField("instruments", [], async (): Promise<Field<instrumentid[]>> => {
      const field = checkboxesField({
        initial: formula.instruments,
        values: opts.allInstruments
          .orderBy((i) => i.description)
          .map((i) => i.id),
        show: function (id: instrumentid): string {
          return (
            opts.allInstruments.find((i) => i.id === id)?.description || ""
          );
        },
        addNoFilterOption: false,
        addAllOption: true,
      });
      return {
        ...field,
        render: () => {
          return wrapSelectboxesInPanel("Cursussen", field.render());
        },
      };
    })
    .addField("planningmode", ["type_"], async ({ type_ }) => {
      const options = match(type_.tag)
        .with("Camp", () => ["Manual" as const])
        .with("Private", () => [
          "Weekly" as const,
          "Biweekly" as const,
          "Flex" as const,
          "Manual" as const,
        ])
        .with("Group", () => [
          "Weekly" as const,
          "Biweekly" as const,
          "Manual" as const,
        ])
        .exhaustive();
      const field = await selectBox<planningmode>({
        values: options,
        initial: options.find((o) => o === formula.planningmode) ?? options[0],
        show: renderPlanningMode,
        label: "Lessenplanning",
      });
      return addInfoUnderField(field, (val) => {
        if (val.tag !== "parsed") {
          return null;
        }
        const text = match(val.parsed)
          .with("Weekly", () =>
            "De lessen worden elke week achter mekaar ingepland.",
          )
          .with("Biweekly", () =>
            "De lessen worden elke twee weken ingepland.",
          )
          .with("Manual", () =>
            "De lessen moeten manueel worden ingepland door een admin. Dit zorgt er ook voor dat herinschrijvingen voor deze formule niet mogelijk zijn.",
          )
          .with("Flex", () =>
            "Bij aanmaak wordt er 1 les ingepland. De volgende lessen worden manueel ingepland door de admin of door de docent via het scherm Planning Lessen. Herinschrijvingen zijn niet mogelijk voor deze formule.",
          )
          .exhaustive();
        return text;
      });
    })
    .addField(
      "startsfromperiod",
      ["type_", "planningmode"],
      ({ type_, planningmode }) =>
        type_.tag === "Camp" ||
        planningmode === "Manual" ||
        planningmode === "Flex"
          ? constantField(false)
          : checkBox({
              initialVal: formula.startsfromperiod,
              class: style.checkbox + style.input_like,
              label: "Deze inschrijving start altijd in het begin van het schooljaar",
            }),
    )
    .addField(
      "showonpublicplanning",
      ["type_", "planningmode"],
      async ({ type_, planningmode }) => {
        const alwaysOff =
          type_.tag === "Group" ||
          type_.tag === "Camp" ||
          planningmode === "Biweekly" ||
          planningmode === "Manual";
        const field = await checkBox({
          initialVal: alwaysOff ? false : formula.showonpublicplanning,
          class: style.checkbox + style.input_like,
          label: "Studenten kunnen deze formule kiezen wanneer ze zich online inschrijven voor een privéles.",
        });
        return {
          ...field,
          render: () => (
            <div style={alwaysOff ? "pointer-events: none; opacity: 0.6;" : ""}>
              {field.render()}
            </div>
          ),
        };
      },
    )
    .addField("periodfee_required", [], async () => {
      if (opts.periodfeeAmountWithoutDiscounts.value <= 0) {
        return constantField(true); // not important then, just default
      } else
        return checkBox({
          initialVal: formula.periodfee_required,
          class: style.checkbox + style.input_like,
          label: "Het jaargeld moet aangerekend worden voor deze formule.".replace(
            "%amount",
            renderCurrency(opts.periodfeeAmountWithoutDiscounts),
          ),
        });
    })
    .addField("questions", [], async () =>
      opts.questionsActivated
        ? makeArrayField<{
            label: string;
            questiontype: questiontype;
            mandatory: boolean;
            select_options: string[] | null;
          }>({
            initialVal: formula.questions,
            // render: oneLineArrayRender((r) => r.label.trim() === "", {}),
            makeSubForm: async (question) => {
              return templateQuestionForm(question, {
                showMandatory: true,
                label: "Vraag",
              });
            },
            makeNew: () => ({
              label: "",
              questiontype: "text" as const,
              mandatory: true,
              select_options: [],
            }),
          })
        : constantField([]),
    )
    .addField("invoicelines", [], async () =>
      formulaInvoicelinesForm(formula.invoicelines, {
        country: opts.country,
        small: false,
      }),
    )
    .addField(
      "max_one_optional_line",
      ["invoicelines"],
      async ({ invoicelines }) => {
        if (invoicelines.filter((il) => il.type_ !== "Normal").length > 1) {
          const cb = await checkBox({
            label: "Maximum 1 korting/optionele lijn",
            initialVal: memoryMaxOneOptionalLine.get(),
            class: style.checkbox + style.input_like,
          });
          cb.s.observe((selected) =>
            selected.tag === "parsed"
              ? memoryMaxOneOptionalLine.set(selected.parsed)
              : {},
          );
          return cb;
        } else {
          return constantField(false);
        }
      },
    )
    .addField(
      "invoicelines_examples",
      ["invoicelines", "numberoflessons", "max_one_optional_line"],
      async ({ invoicelines, numberoflessons, max_one_optional_line }) => {
        if (!invoicelines.some((il) => il.type_ !== "Normal")) {
          return constantField(null);
        }

        const normalinvoicelines = invoicelines.filter(
          (il) => il.type_ === "Normal",
        );
        const normalinvoicelinessumwithvat = normalinvoicelines.reduce(
          (acc, il) => acc.add(il.amount_with_vat),
          currency(0),
        );
        const normalinvoicelinessumwithoutvat = normalinvoicelines.reduce(
          (acc, il) => acc.add(calculateAmountWithoutVat(il)),
          currency(0),
        );
        let variations: { optionalinvoicelines: invoicelineFormula[] }[] = [
          { optionalinvoicelines: [] },
        ];
        for (const optil of invoicelines.filter(
          (il) => il.type_ !== "Normal",
        )) {
          if (max_one_optional_line === false) {
            // make permutations
            variations = variations.concat(
              variations.flatMap((existingvar) => ({
                optionalinvoicelines: existingvar.optionalinvoicelines.concat([
                  optil,
                ]),
              })),
            );
          } else {
            variations = variations.concat({ optionalinvoicelines: [optil] });
          }
        }

        return constantFieldWithRender(
          null,
          <div style="text-align: right;">
            <div
              className={style.card + style.formsection_informational}
              style=" border: 1px solid var(--grey-200); "
            >
              <table className={style.table_condensed}>
                <tr>
                  <th>{"Keuze"}</th>
                  <th>
                    {opts.country === "Netherlands"
                      ? "Totaal > 21y"
                      : "Totaal"}
                  </th>
                  {opts.country === "Netherlands" ? (
                    <th>{"Totaal < 21y"}</th>
                  ) : (
                    []
                  )}
                </tr>
                {variations.map((vari, i) => {
                  const description =
                    vari.optionalinvoicelines.length === 0
                      ? [
                          <li style="font-style: italic;">
                            {"Geen optionele lijnen"}
                          </li>,
                        ]
                      : vari.optionalinvoicelines
                          .orderBy((il) => il.description)
                          .map((il) => <li>{il.description}</li>);
                  const total_with_vat = normalinvoicelinessumwithvat.add(
                    vari.optionalinvoicelines.reduce((acc, il) => {
                      return acc
                        .add(il.amount_with_vat)
                        .multiply(
                          il.chargemode === "Enrollment" ? 1 : numberoflessons,
                        );
                    }, currency(0)),
                  );
                  const total_without_vat = normalinvoicelinessumwithoutvat.add(
                    vari.optionalinvoicelines.reduce((acc, il) => {
                      return acc.add(
                        calculateAmountWithoutVat(il).multiply(
                          il.chargemode === "Enrollment" ? 1 : numberoflessons,
                        ),
                      );
                    }, currency(0)),
                  );
                  return (
                    <tr
                      style={
                        i % 2 === 0 ? "background-color: var(--grey-100);" : ""
                      }
                    >
                      <td style="text-align: left;">
                        <ul>{description}</ul>
                      </td>
                      <td>{renderCurrency(total_with_vat)}</td>
                      {opts.country === "Netherlands" ? (
                        <td>{renderCurrency(total_without_vat)}</td>
                      ) : (
                        []
                      )}
                    </tr>
                  );
                })}
              </table>
            </div>
          </div>,
        );
      },
    )
    .addField("payment_schemes", [], async () => {
      const values = opts.paymentSchemes.map((ps) => ps.id);
      const field = checkboxesField({
        initial: formula.payment_schemes,
        values,
        show: (id) => {
          return opts.paymentSchemes.find((ps) => ps.id === id)?.label || "";
        },
        addNoFilterOption: false,
        addAllOption: true,
      });
      return {
        ...field,
        render: () => {
          return wrapSelectboxesInPanel(
            "Betalingsschema's",
            field.render(),
          );
        },
      };
    })
    .addField("allow_trial_lessons", ["type_"], async ({ type_ }) => {
      if (type_.tag === "Private") {
        return constantField(false);
      } else {
        const field = await checkBox({
          initialVal: formula.allow_trial_lessons,
          class: style.checkbox + style.input_like,
          label: "Proefles toelaten",
        });
        return {
          ...field,
          render: () => {
            return (
              <div>
                <label>{"Proefles"}</label>
                {field.render()}
              </div>
            );
          },
        };
      }
    })
    .addField(
      "students_can_only_make_trial_lessons",
      ["allow_trial_lessons"],
      async ({ allow_trial_lessons }) => {
        if (allow_trial_lessons === false) {
          return constantField(false);
        } else {
          const field = await checkBox({
            initialVal: formula.students_can_only_make_trial_lessons,
            class: style.checkbox + style.input_like,
            label: "Studenten kunnen online enkel proeflessen aanvragen",
          });
          return {
            ...field,
            render: () => {
              return (
                <div>
                  <label>&nbsp;</label>
                  {field.render()}
                </div>
              );
            },
          };
        }
      },
    )
    .addField(
      "trial_lesson_price",
      ["allow_trial_lessons"],
      ({ allow_trial_lessons }) => {
        if (!allow_trial_lessons) {
          return constantField(currency(0));
        } else {
          return currencyBox(formula.trial_lesson_price, {
            label: "Kostprijs proefles",
          });
        }
      },
    )
    .addField(
      "trial_lesson_vat",
      ["allow_trial_lessons"],
      ({ allow_trial_lessons }) => {
        if (!allow_trial_lessons) {
          return constantField(0);
        } else {
          return numberBox(formula.trial_lesson_vat, {
            label: "Proefles BTW %",
          });
        }
      },
    )
    .addField(
      "trial_lesson_payment_scheme",
      ["allow_trial_lessons"],
      ({ allow_trial_lessons }) => {
        if (!allow_trial_lessons) {
          return constantField(null);
        } else {
          const values = mapPartial(opts.paymentSchemes, (ps) =>
            isNil(ps.payment_scheme_non_periodic_id)
              ? null
              : {
                  id: ps.payment_scheme_non_periodic_id,
                  label: ps.label,
                },
          );
          return selectBoxNullable({
            initial:
              values.find(
                (ps) => ps.id === formula.trial_lesson_payment_scheme_id,
              ) || null,
            values: values,
            label: "Betaalschema",
            show: (ps) => ps.label,
          });
        }
      },
    )
    .addField(
      "trial_lesson_payment_scheme_id",
      ["trial_lesson_payment_scheme"],
      async ({ trial_lesson_payment_scheme }) => {
        if (!isNil(trial_lesson_payment_scheme)) {
          return constantField(trial_lesson_payment_scheme.id);
        } else {
          return constantField(null);
        }
      },
    )
    .addField("group_waitinglist_active", [], () =>
      constantField(formula.group_waitinglist_active),
    )
    .build({
      render: (fields) => {
        return (
          <div>
            <article className={style.equal_columns}>
              {fields.description()}
              {fields.color()}
            </article>
            <article>{fields.comments()}</article>
            <hr />
            <article className={style.equal_columns}>
              <div>
                <article>{fields.formulatypetag()}</article>
                <article>{fields.maxnumberofstudents()}</article>
                <article>{fields.numberoflessons()}</article>
                <article>{fields.lessonduration()}</article>
                <section>{fields.planningmode()}</section>
                <article>{fields.startsfromperiod()}</article>
                <article>{fields.showonpublicplanning()}</article>
                <article>{fields.termsurl()}</article>
              </div>
              {fields.instruments()}
            </article>

            <hr />

            {opts.questionsActivated ? (
              <div>
                <section>
                  <label for="">
                    {"Vragenlijst tijdens online inschrijving"}
                  </label>
                  {fields.questions()}
                </section>

                <hr />
              </div>
            ) : (
              []
            )}

            <section>
              {fields.invoicelines()}
              {fields.max_one_optional_line()}
              {fields.invoicelines_examples()}
              {fields.periodfee_required()}
            </section>

            <hr />

            {fields.payment_schemes()}

            <hr />

            <section>
              <div
                className={style.equal_columns}
                style="align-items: flex-end;"
              >
                {fields.allow_trial_lessons()}
                {fields.students_can_only_make_trial_lessons()}
              </div>
              <div
                className={style.equal_columns}
                style="align-items: flex-end;"
              >
                {fields.trial_lesson_price()}
                {fields.trial_lesson_vat()}
                {fields.trial_lesson_payment_scheme()}
              </div>
            </section>
          </div>
        );
      },
    });

  return (
    <div>
      {!isNil(formula.id) ? (
        <section style="text-align: right">
          <a
            href={routes.config_formulasRoute.link({
              formulaid: formula.id,
              copy: true,
            })}
            className={style.text_with_icon}
            style="display: inline-block; text-decoration: underline;"
          >
            <i className={FA.icon_clipboard_solid}></i>
            {"Kopieer formule"}
          </a>
        </section>
      ) : (
        []
      )}

      {form.render()}

      <div style="text-align: right">
        <a
          href={routes.config_formulas_listRoute.link()}
          className={
            style.button + style.button_em_down + style.a_around_button
          }
        >
          <i className={FA.icon_arrow_left}></i>
          {"Terug"}
        </a>

        {id !== null ? (
          renderDeleteButton({
            label: "Verwijder formule",
            confirmationLabel: "Ben je zeker dat je deze formule wilt verwijderen?",
            doDelete: () =>
              rpc(routes.deleteFormulaSpec, undefined, {
                formulaid: id,
              }),
            redirectTo: routes.config_formulas_listRoute.link(),
          })
        ) : (
          <span></span>
        )}

        {renderSaveButton({
          formS: form.s,
          save: async function (form) {
            await rpc(saveFormulaSpec, undefined, form);
            return {
              tag: "success",
              redirectUrl: routes.config_formulas_listRoute.link(),
              timeout: 1500,
            };
          },
          reload: false,
        })}
      </div>
    </div>
  );
}

export function templateQuestionForm(
  question: {
    label: string;
    questiontype: questiontype;
    mandatory: boolean;
    select_options: string[] | null;
  },
  opts: { showMandatory: boolean; label: string },
) {
  return new Form({})
    .addField("label", [], () =>
      textBox({
        initialVal: question.label,
        label: opts.label,
        type: "textarea",
        style: "height: 80px;",
        validations: [mandatoryValidation],
      }),
    )
    .addField("questiontype", [], () => {
      const options = [
        "text" as const,
        "boolean" as const,
        "select" as const,
        "multiselect" as const,
        "number" as const,
      ];
      return selectBox({
        values: options,
        initial: question.questiontype,
        show: (t) =>
          t === "text"
            ? "Tekst"
            : t === "boolean"
            ? "Ja/nee"
            : t === "select"
            ? "Selectie"
            : t === "multiselect"
            ? "Selectie met meerdere mogelijkheden"
            : t === "number"
            ? "Getal"
            : checkAllCasesHandled(t),
        label: "Type",
      });
    })
    .addField("mandatory", ["questiontype"], ({ questiontype }) =>
      questiontype === "multiselect"
        ? constantField(false)
        : checkBox({
            initialVal: question.mandatory,
            label: "Verplicht",
            class: style.checkbox + style.input_like,
          }),
    )
    .addField("select_options", ["questiontype"], async ({ questiontype }) => {
      if (questiontype === "select" || questiontype === "multiselect") {
        const field = await makeArrayField({
          initialVal: question.select_options || [],
          render: oneLineArrayRender((t) => t.trim() === "", {
            minimumOne: true,
          }),
          makeSubForm: (t: string) =>
            textBox({
              initialVal: t,
              validations: [mandatoryValidation],
            }),
          makeNew: () => "",
        });
        return {
          ...field,
          render: () => {
            return (
              <article>
                <label for="">{"Opties"}</label>
                {field.render()}
              </article>
            );
          },
        };
      } else {
        return constantField([]);
      }
    })
    .build({
      render: (fields) => (
        <div>
          {fields.label()}
          <div className={style.equal_columns} style="align-items: flex-end;">
            {fields.questiontype()}
            {opts.showMandatory ? (
              <div style="position: relative; top: -4px;">
                {fields.mandatory()}
              </div>
            ) : (
              []
            )}
          </div>
          {fields.select_options()}
        </div>
      ),
    });
}
